import React, { useState, useCallback } from 'react';
import Papa from 'papaparse';
import { supabase } from '../../../lib/supabase';
import { companyService } from '../../../services/companyService';
import { setupService } from '../../../services/setupService';
import { workerCertificationService } from '../../../services/workerCertificationService';
import Icon from '../../../components/AppIcon';
import TemplateDownloadButton from './TemplateDownloadButton';
import DropZone from './DropZone';
import ValidationTable from './ValidationTable';
import PreviewTable from './PreviewTable';
import { normalizeEnum } from '../../../utils/normalizeEnum';

// ─── Validation helpers ───────────────────────────────────────────────────────
// Accept ISO (YYYY-MM-DD) and UK-style DD-MM-YYYY / DD/MM/YYYY. Normalise to
// YYYY-MM-DD so the payload sent to Postgres is always canonical. Anything
// else is returned untouched so the DATE_RE check below still fails it.
const normaliseDate = (v) => {
  if (!v) return v;
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return s;
};
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DATE_FORMAT_HINT = 'YYYY-MM-DD or DD-MM-YYYY';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SITE_STATUS_VALUES = ['planning', 'active', 'on_hold', 'completed', 'archived'];
const TASK_STATUS_VALUES = ['pending', 'in_progress', 'completed', 'blocked'];
const TASK_CATEGORY_VALUES = ['Safety', 'Operations', 'Compliance', 'Maintenance', 'Inspection', 'Admin'];
const DOC_CATEGORY_VALUES = ['rams', 'insurance', 'training', 'plant', 'inspection_report', 'subcontractor', 'equipment_certificate'];
const SNAG_STATUS_VALUES = ['open', 'in_progress', 'resolved', 'completed'];
const SNAG_PRIORITY_VALUES = ['low', 'medium', 'high', 'critical'];
const USER_ROLE_VALUES = ['admin', 'site_manager', 'worker'];
const MAX_USER_IMPORT_ROWS = 100;

const COLUMNS = {
  sites: ['site_name', 'site_reference', 'address_line1', 'city', 'postcode', 'client_name', 'client_contact_person', 'client_email', 'client_phone', 'status', 'start_date', 'end_date'],
  users: ['full_name', 'email', 'role'],
  workers: ['full_name', 'email', 'phone', 'trade', 'cscs_card_number', 'cscs_expiry_date'],
  tasks: ['title', 'description', 'site_name', 'task_date', 'category', 'status', 'photo_required'],
  certifications: ['workerEmail', 'certificationType', 'certificateNumber', 'issuedDate', 'expiryDate'],
  documents: ['documentName', 'category', 'siteName', 'issueDate', 'expiryDate', 'owner'],
  snags: ['siteName', 'description', 'priority', 'assignedWorker', 'status', 'dueDate'],
};

const validateRow = (row, type, rowIndex) => {
  const errors = [];
  const r = row;

  if (type === 'users') {
    if (!r?.full_name?.trim()) errors?.push('full_name required');
    if (!r?.email?.trim()) errors?.push('email required');
    else if (!EMAIL_RE?.test(r?.email?.trim())) errors?.push('email invalid');
    const role = normalizeEnum(r?.role);
    r.role = role;
    if (!role) errors?.push('role required');
    else if (!USER_ROLE_VALUES?.includes(role)) errors?.push(`role must be one of: ${USER_ROLE_VALUES?.join(', ')}`);
  }

  if (type === 'sites') {
    if (!r?.site_name?.trim()) errors?.push('site_name required');
    if (!r?.address_line1?.trim()) errors?.push('address_line1 required');
    if (!r?.city?.trim()) errors?.push('city required');
    if (!r?.postcode?.trim()) errors?.push('postcode required');
    if (!r?.client_name?.trim()) errors?.push('client_name required');
    if (r?.client_email && !EMAIL_RE?.test(r?.client_email)) errors?.push('client_email invalid');
    if (r?.status) {
      r.status = normalizeEnum(r?.status);
      if (!SITE_STATUS_VALUES?.includes(r?.status)) errors?.push(`status must be one of: ${SITE_STATUS_VALUES?.join(', ')}`);
    }
    if (!r?.start_date?.trim()) {
      errors?.push('start_date required');
    } else {
      r.start_date = normaliseDate(r?.start_date);
      if (!DATE_RE?.test(r?.start_date)) errors?.push(`start_date must be ${DATE_FORMAT_HINT}`);
    }
    if (!r?.end_date?.trim()) {
      errors?.push('end_date required');
    } else {
      r.end_date = normaliseDate(r?.end_date);
      if (!DATE_RE?.test(r?.end_date)) errors?.push(`end_date must be ${DATE_FORMAT_HINT}`);
    }
  }

  if (type === 'workers') {
    if (!r?.full_name?.trim()) errors?.push('full_name required');
    if (!r?.email?.trim()) errors?.push('email required');
    else if (!EMAIL_RE?.test(r?.email)) errors?.push('email invalid');
    if (r?.cscs_expiry_date) {
      r.cscs_expiry_date = normaliseDate(r?.cscs_expiry_date);
      if (!DATE_RE?.test(r?.cscs_expiry_date)) errors?.push(`cscs_expiry_date must be ${DATE_FORMAT_HINT}`);
    }
  }

  if (type === 'tasks') {
    if (!r?.title?.trim()) errors?.push('title required');
    if (!r?.site_name?.trim()) errors?.push('site_name required');
    if (!r?.task_date?.trim()) {
      errors?.push('task_date required');
    } else {
      r.task_date = normaliseDate(r?.task_date);
      if (!DATE_RE?.test(r?.task_date)) errors?.push(`task_date must be ${DATE_FORMAT_HINT}`);
    }
    if (r?.status && !TASK_STATUS_VALUES?.includes(r?.status)) errors?.push(`status must be one of: ${TASK_STATUS_VALUES?.join(', ')}`);
    if (r?.category && !TASK_CATEGORY_VALUES?.includes(r?.category)) errors?.push(`category must be one of: ${TASK_CATEGORY_VALUES?.join(', ')}`);
  }

  if (type === 'certifications') {
    if (!r?.workerEmail?.trim()) errors?.push('workerEmail required');
    else if (!EMAIL_RE?.test(r?.workerEmail)) errors?.push('workerEmail invalid');
    if (!r?.certificationType?.trim()) errors?.push('certificationType required');
    if (!r?.expiryDate?.trim()) {
      errors?.push('expiryDate required');
    } else {
      r.expiryDate = normaliseDate(r?.expiryDate);
      if (!DATE_RE?.test(r?.expiryDate)) errors?.push(`expiryDate must be ${DATE_FORMAT_HINT}`);
    }
    if (r?.issuedDate) {
      r.issuedDate = normaliseDate(r?.issuedDate);
      if (!DATE_RE?.test(r?.issuedDate)) errors?.push(`issuedDate must be ${DATE_FORMAT_HINT}`);
    }
  }

  if (type === 'documents') {
    if (!r?.documentName?.trim()) errors?.push('documentName required');
    if (!r?.category?.trim()) errors?.push('category required');
    else if (!DOC_CATEGORY_VALUES?.includes(r?.category?.trim()?.toLowerCase())) errors?.push(`category must be one of: ${DOC_CATEGORY_VALUES?.join(', ')}`);
    if (!r?.siteName?.trim()) errors?.push('siteName required');
    if (!r?.owner?.trim()) errors?.push('owner required');
    if (!r?.expiryDate?.trim()) {
      errors?.push('expiryDate required');
    } else {
      r.expiryDate = normaliseDate(r?.expiryDate);
      if (!DATE_RE?.test(r?.expiryDate)) errors?.push(`expiryDate must be ${DATE_FORMAT_HINT}`);
    }
    if (r?.issueDate) {
      r.issueDate = normaliseDate(r?.issueDate);
      if (!DATE_RE?.test(r?.issueDate)) errors?.push(`issueDate must be ${DATE_FORMAT_HINT}`);
    }
  }

  if (type === 'snags') {
    if (!r?.siteName?.trim()) errors?.push('siteName required');
    if (!r?.description?.trim()) errors?.push('description required');
    if (r?.priority && !SNAG_PRIORITY_VALUES?.includes(r?.priority?.trim()?.toLowerCase())) errors?.push(`priority must be one of: ${SNAG_PRIORITY_VALUES?.join(', ')}`);
    if (r?.status && !SNAG_STATUS_VALUES?.includes(r?.status?.trim()?.toLowerCase())) errors?.push(`status must be one of: ${SNAG_STATUS_VALUES?.join(', ')}`);
    if (r?.dueDate) {
      r.dueDate = normaliseDate(r?.dueDate);
      if (!DATE_RE?.test(r?.dueDate)) errors?.push(`dueDate must be ${DATE_FORMAT_HINT}`);
    }
  }

  return { ...row, _rowIndex: rowIndex, _errors: errors };
};

// ─── Import functions ─────────────────────────────────────────────────────────
const importSites = async (validRows, companyId, userId) => {
  const errors = [];

  // ── Pre-check (a) + (b): dedupe against existing sites and within the CSV ──
  // Fetch the company's non-archived sites into a normalised-name → id map.
  // The (company_id + non-archived) scope mirrors the partial unique index
  // sites_company_normalized_name_unique (R4U3iRGh PR 1) and the
  // importUsers/importWorkers "already in company → skipped" pattern.
  const { data: existingSites } = await supabase
    ?.from('sites')
    ?.select('id, site_name')
    ?.eq('company_id', companyId)
    ?.neq('status', 'archived');
  const existingMap = {};
  existingSites?.forEach(s => { existingMap[s?.site_name?.trim()?.toLowerCase()] = s?.id; });

  // Single pass folds the in-CSV dedupe into the existing-site check: a name
  // that's already in the company is skipped; a name seen earlier in this same
  // CSV is flagged as an in-file duplicate on its second occurrence.
  const seenInCsv = new Set();
  const deduped = [];
  for (const r of validRows) {
    const name = r?.site_name?.trim();
    const normalized = name?.toLowerCase();
    if (existingMap?.[normalized]) {
      errors?.push({ row: r?._rowIndex, message: `site "${name}" already exists for this company, skipped` });
      continue;
    }
    if (seenInCsv?.has(normalized)) {
      errors?.push({ row: r?._rowIndex, message: `duplicate site name within CSV: "${name}"` });
      continue;
    }
    seenInCsv?.add(normalized);
    deduped?.push(r);
  }

  // ── Pre-check (c): plan-limit gate (incremental active-site capacity) ──
  // Only rows inserted as status='active' consume a plan slot — get_active_site_usage
  // counts strictly status='active', and the insert below defaults a blank
  // status to 'planning'. So the gate counts/rejects by *effective insert
  // status* (blank → planning → not gated), keeping it consistent with what
  // actually consumes capacity. max_sites=null means an unmetered plan: skip.
  let toInsert = deduped;
  const { data: usage } = await companyService?.getActiveSiteUsage(companyId);
  const maxSites = usage?.max_sites;
  if (maxSites != null) {
    const remainingCapacity = Math.max(0, maxSites - (usage?.active_count || 0));
    let activeAccepted = 0;
    toInsert = [];
    for (const r of deduped) {
      const willBeActive = (r?.status?.trim() || 'planning') === 'active';
      if (willBeActive && activeAccepted >= remainingCapacity) {
        errors?.push({ row: r?._rowIndex, message: `plan limit reached: ${maxSites} active sites allowed, this row would exceed` });
        continue;
      }
      if (willBeActive) activeAccepted++;
      toInsert?.push(r);
    }
  }

  // Everything got deduped/limited away — nothing to insert, but the per-row
  // skip reasons still flow back to the toast.
  if (!toInsert?.length) {
    return { imported: 0, errors };
  }

  const records = toInsert?.map(r => ({
    company_id: companyId,
    site_name: r?.site_name?.trim(),
    site_reference: r?.site_reference?.trim() || null,
    address_line1: r?.address_line1?.trim(),
    city: r?.city?.trim(),
    postcode: r?.postcode?.trim(),
    client_name: r?.client_name?.trim(),
    client_contact_person: r?.client_contact_person?.trim() || null,
    client_email: r?.client_email?.trim() || null,
    client_phone: r?.client_phone?.trim() || null,
    status: r?.status?.trim() || 'planning',
    start_date: r?.start_date?.trim() || null,
    end_date: r?.end_date?.trim() || null,
    created_by: userId,
  }));
  // Bulk insert: PostgREST aborts the whole batch on the first failure, so
  // we cannot surface per-row reasons here without N round-trips. Return the
  // single error against the first row's index so the toast still tells the
  // admin what went wrong instead of green-checking through 0 imports.
  const { data, error } = await supabase?.from('sites')?.insert(records)?.select('id');
  if (error) {
    // The partial unique index (R4U3iRGh PR 1) is the DB-level backstop. If a
    // concurrent import slips a duplicate in between the fetch above and this
    // insert, Postgres raises 23505 and aborts the batch — surface it as a
    // clean dedupe message instead of a raw Postgres error.
    const isUnique = error?.code === '23505';
    errors?.push({
      row: toInsert?.[0]?._rowIndex,
      message: isUnique
        ? 'a site with this name already exists for this company'
        : (error?.message || 'Sites insert failed'),
    });
    return { imported: 0, errors };
  }
  return { imported: data?.length || 0, errors };
};

// Re-routed through the canonical invite path (OcTdmSo9). Mirrors
// importUsers: pre-resolve existing profiles by email (NOT company-scoped,
// so cross-company collisions are detected), invite NEW workers via
// setupService.inviteTeamMembersPerRole, then persist the extra worker
// fields into their proper homes AFTER the invite — trade/phone via the
// scoped set_worker_import_attributes RPC (a client UPDATE of another
// user's profile is RLS-blocked), CSCS via worker_certifications.
// Induction stripped per Heidi 19 May — per-site induction is tracked in
// site_induction_completions; the RPC's induction params stay dormant.
// Returns structured per-row outcome { invited, skipped, errors, warnings }.
const importWorkers = async (validRows, companyId) => {
  const emails = [...new Set(validRows?.map(r => r?.email?.trim()?.toLowerCase()))];
  // jyyb8AfJ — project deactivated_at so we can distinguish a "previously
  // deactivated by an admin" row from an "already-a-member" row. Different
  // remediation: deactivated needs explicit reactivate from Users & Teams.
  const { data: existingProfiles } = await supabase
    ?.from('user_profiles')
    ?.select('email, company_id, is_active, deactivated_at')
    ?.in('email', emails);

  const profileMap = {};
  existingProfiles?.forEach(p => { profileMap[p?.email?.toLowerCase()] = p; });

  const toInvite = [];
  const rowByEmail = {};
  let skipped = 0;
  const errors = [];
  const warnings = [];

  for (const r of validRows) {
    const email = r?.email?.trim()?.toLowerCase();
    const existing = profileMap?.[email];
    // jyyb8AfJ — deactivated rows in this company surface as a warning and
    // are NOT implicitly reactivated. Admin reactivates manually from the
    // Users & Teams page (Heidi's product default).
    if (existing?.company_id === companyId && existing?.deactivated_at != null) {
      skipped++;
      warnings?.push(`${email}: previously deactivated — reactivate from Users & Teams to re-enable, then re-import`);
      continue;
    }
    if (existing?.company_id === companyId && existing?.is_active) {
      skipped++;
      continue;
    }
    if (existing?.company_id && existing?.company_id !== companyId) {
      errors?.push(`${email}: already registered under another company`);
      continue;
    }
    rowByEmail[email] = r;
    toInvite?.push({ email: r?.email?.trim(), name: r?.full_name?.trim(), role: 'worker' });
  }

  let invited = 0;
  const invitedEmails = [];
  if (toInvite?.length > 0) {
    const { data: results, error: inviteError } = await setupService?.inviteTeamMembersPerRole(companyId, toInvite);
    if (inviteError) throw inviteError;
    (results || [])?.forEach(res => {
      const email = res?.email?.trim()?.toLowerCase();
      if (res?.success) {
        invited++;
        invitedEmails?.push(email);
      } else {
        errors?.push(`${email}: ${res?.error || 'invite failed'}`);
      }
    });
  }

  if (invitedEmails?.length > 0) {
    const { data: profs } = await supabase
      ?.from('user_profiles')
      ?.select('id, email')
      ?.eq('company_id', companyId)
      ?.in('email', invitedEmails);
    const idByEmail = {};
    profs?.forEach(p => { idByEmail[p?.email?.toLowerCase()] = p?.id; });

    for (const email of invitedEmails) {
      const r = rowByEmail?.[email];
      const uid = idByEmail?.[email];
      if (!uid) {
        warnings?.push(`${email}: invited, but extra details not yet applied (re-import to apply)`);
        continue;
      }

      const { data: rowsUpdated, error: attrErr } = await supabase?.rpc('set_worker_import_attributes', {
        p_user_id: uid,
        p_trade: r?.trade?.trim() || null,
        // Induction stripped per Heidi 19 May. RPC signature is left intact
        // (out of scope to change); it has no arg defaults, so pass null —
        // the RPC's COALESCE(p_x, col) makes null a no-op (writes nothing).
        p_induction_date: null,
        p_induction_completed: null,
        p_phone: r?.phone?.trim() || null,
      });
      if (attrErr || rowsUpdated === 0) {
        warnings?.push(`${email}: invited, but trade details could not be saved`);
      }

      const cscsNumber = r?.cscs_card_number?.trim();
      const cscsExpiry = r?.cscs_expiry_date?.trim();
      if (cscsNumber) {
        if (!cscsExpiry) {
          warnings?.push(`${email}: CSCS card number present but expiry missing — CSCS not recorded`);
        } else {
          const { data: existingCerts } = await supabase
            ?.from('worker_certifications')
            ?.select('id')
            ?.eq('worker_user_id', uid)
            ?.eq('certificate_type', 'CSCS')
            ?.limit(1);
          if (!existingCerts?.length) {
            const { error: certErr } = await workerCertificationService?.addCertification({
              workerUserId: uid,
              companyId,
              certificateType: 'CSCS',
              certificateNumber: cscsNumber,
              expiryDate: cscsExpiry,
            });
            if (certErr) {
              warnings?.push(`${email}: invited, but CSCS certification could not be saved`);
            }
          }
        }
      }
    }
  }

  return { invited, skipped, errors, warnings };
};

const importTasks = async (validRows, companyId, userId) => {
  // Fetch all sites for this company to resolve site_name → site_id.
  // Trim on the DB side too — CSV side trims, so a stored name with stray
  // whitespace (e.g. "Thame Park Road ") would otherwise never match.
  const { data: sites } = await supabase?.from('sites')?.select('id, site_name')?.eq('company_id', companyId);
  const siteMap = {};
  sites?.forEach(s => { siteMap[s?.site_name?.trim()?.toLowerCase()] = s?.id; });

  let imported = 0;
  const errors = [];
  for (const r of validRows) {
    const siteId = siteMap?.[r?.site_name?.trim()?.toLowerCase()];
    if (!siteId) {
      errors?.push({ row: r?._rowIndex, message: `site_name "${r?.site_name?.trim()}" not found in this company` });
      continue;
    }
    const photoRequired = r?.photo_required?.toLowerCase() === 'true';
    const { error } = await supabase?.from('daily_tasks')?.insert({
      company_id: companyId,
      site_id: siteId,
      title: r?.title?.trim(),
      description: r?.description?.trim() || '',
      task_date: r?.task_date?.trim(),
      category: r?.category?.trim() || 'Operations',
      status: r?.status?.trim() || 'pending',
      photo_required: photoRequired,
      created_by_user_id: userId,
      assigned_to_user_id: userId,
    });
    if (error) {
      errors?.push({ row: r?._rowIndex, message: error?.message || 'Insert failed' });
    } else {
      imported++;
    }
  }
  return { imported, errors };
};

const importCertifications = async (validRows, companyId, userId) => {
  // Resolve worker emails to user_profile IDs
  const emails = [...new Set(validRows?.map(r => r?.workerEmail?.trim()?.toLowerCase()))];
  const { data: profiles } = await supabase
    ?.from('user_profiles')
    ?.select('id, email')
    ?.eq('company_id', companyId)
    ?.in('email', emails);

  const emailToUserId = {};
  profiles?.forEach(p => { emailToUserId[p?.email?.toLowerCase()] = p?.id; });

  let imported = 0;
  const errors = [];
  for (const r of validRows) {
    const workerUserId = emailToUserId?.[r?.workerEmail?.trim()?.toLowerCase()];
    if (!workerUserId) {
      errors?.push({ row: r?._rowIndex, message: `workerEmail "${r?.workerEmail?.trim()}" not found in this company` });
      continue;
    }
    const { error } = await supabase?.from('worker_certifications')?.insert({
      company_id: companyId,
      worker_user_id: workerUserId,
      certificate_type: r?.certificationType?.trim(),
      certificate_number: r?.certificateNumber?.trim() || null,
      expiry_date: r?.expiryDate?.trim(),
      uploaded_by: userId,
    });
    if (error) {
      errors?.push({ row: r?._rowIndex, message: error?.message || 'Insert failed' });
    } else {
      imported++;
    }
  }
  return { imported, errors };
};

const importDocuments = async (validRows, companyId, userId) => {
  const { data: sites } = await supabase?.from('sites')?.select('id, site_name')?.eq('company_id', companyId);
  const siteMap = {};
  sites?.forEach(s => { siteMap[s?.site_name?.trim()?.toLowerCase()] = s?.id; });

  const today = new Date()?.toISOString()?.split('T')?.[0];

  let imported = 0;
  const errors = [];
  for (const r of validRows) {
    const siteId = siteMap?.[r?.siteName?.trim()?.toLowerCase()];
    if (!siteId) {
      errors?.push({ row: r?._rowIndex, message: `siteName "${r?.siteName?.trim()}" not found for this company` });
      continue;
    }

    const category = r?.category?.trim()?.toLowerCase();
    const expiryDate = r?.expiryDate?.trim();
    const issueDate = r?.issueDate?.trim() || today;

    // Compute days_until_expiry from expiry date (validator already enforced presence + format)
    const diff = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    const daysUntilExpiry = diff;

    // Determine document status from expiry
    let status = 'valid';
    if (daysUntilExpiry < 0) status = 'expired';
    else if (daysUntilExpiry <= 7) status = 'expiring-critical';
    else if (daysUntilExpiry <= 30) status = 'expiring-soon';

    const { error } = await supabase?.from('documents')?.insert({
      company_id: companyId,
      site_id: siteId,
      title: r?.documentName?.trim(),
      category,
      owner: r?.owner?.trim(),
      issue_date: issueDate,
      expiry_date: expiryDate,
      days_until_expiry: daysUntilExpiry,
      status,
      file_path: 'csv-import/placeholder',
      uploaded_by: userId,
    });
    if (error) {
      errors?.push({ row: r?._rowIndex, message: error?.message || 'Insert failed' });
    } else {
      imported++;
    }
  }
  return { imported, errors };
};

const importSnags = async (validRows, companyId, userId) => {
  const { data: sites } = await supabase?.from('sites')?.select('id, site_name')?.eq('company_id', companyId);
  const siteMap = {};
  sites?.forEach(s => { siteMap[s?.site_name?.trim()?.toLowerCase()] = s?.id; });

  // Optionally resolve assignedWorker emails
  const workerEmails = validRows
    ?.map(r => r?.assignedWorker?.trim()?.toLowerCase())
    ?.filter(Boolean);

  const emailToUserId = {};
  if (workerEmails?.length > 0) {
    const { data: profiles } = await supabase
      ?.from('user_profiles')
      ?.select('id, email')
      ?.eq('company_id', companyId)
      ?.in('email', workerEmails);
    profiles?.forEach(p => { emailToUserId[p?.email?.toLowerCase()] = p?.id; });
  }

  let imported = 0;
  const errors = [];
  for (const r of validRows) {
    const siteId = siteMap?.[r?.siteName?.trim()?.toLowerCase()];
    if (!siteId) {
      errors?.push({ row: r?._rowIndex, message: `siteName "${r?.siteName?.trim()}" not found in this company` });
      continue;
    }

    const assignedWorkerEmail = r?.assignedWorker?.trim()?.toLowerCase();
    const assignedTo = assignedWorkerEmail ? (emailToUserId?.[assignedWorkerEmail] || null) : null;

    const { error } = await supabase?.from('snags')?.insert({
      company_id: companyId,
      site_id: siteId,
      title: r?.description?.trim()?.substring(0, 100),
      description: r?.description?.trim(),
      priority: r?.priority?.trim()?.toLowerCase() || 'medium',
      status: r?.status?.trim()?.toLowerCase() || 'open',
      assigned_to: assignedTo,
      due_date: r?.dueDate?.trim() || null,
      created_by: userId,
      photo_paths: [],
    });
    if (error) {
      errors?.push({ row: r?._rowIndex, message: error?.message || 'Insert failed' });
    } else {
      imported++;
    }
  }
  return { imported, errors };
};

const importUsers = async (validRows, companyId) => {
  // Check for existing emails in same company (skip) or other companies (error)
  const emails = validRows?.map(r => r?.email?.trim()?.toLowerCase());
  // jyyb8AfJ — see importWorkers for rationale on the deactivated_at projection.
  const { data: existingProfiles } = await supabase
    ?.from('user_profiles')
    ?.select('email, company_id, is_active, deactivated_at')
    ?.in('email', emails);

  const profileMap = {};
  existingProfiles?.forEach(p => { profileMap[p?.email?.toLowerCase()] = p; });

  const toInvite = [];
  let skipped = 0;
  const errors = [];
  const warnings = [];

  for (const r of validRows) {
    const email = r?.email?.trim()?.toLowerCase();
    const existing = profileMap?.[email];
    if (existing?.company_id === companyId && existing?.deactivated_at != null) {
      skipped++;
      warnings?.push(`${email}: previously deactivated — reactivate from Users & Teams to re-enable, then re-import`);
      continue;
    }
    if (existing?.company_id === companyId && existing?.is_active) {
      skipped++;
      continue;
    }
    if (existing?.company_id && existing?.company_id !== companyId) {
      errors?.push(`${email}: already registered under another company`);
      continue;
    }
    toInvite?.push({
      email: r?.email?.trim(),
      name: r?.full_name?.trim(),
      role: r?.role?.trim()?.toLowerCase(),
    });
  }

  let invited = 0;
  if (toInvite?.length > 0) {
    const { data: results, error: inviteError } = await setupService?.inviteTeamMembersPerRole(companyId, toInvite);
    if (inviteError) throw inviteError;
    invited = (results || [])?.filter(r => r?.success)?.length || 0;
  }

  return { invited, skipped, errors, warnings };
};

// ─── Component ────────────────────────────────────────────────────────────────
const ImportTab = ({ type, onImportSuccess }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const columns = COLUMNS?.[type] || [];

  const handleFileSelected = useCallback((file) => {
    setFileName(file?.name);
    setRows([]);
    setImportResult(null);
    setIsProcessing(true);

    Papa?.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header?.replace(/\s*\(.*\)\s*$/, '')?.trim(),
      complete: (results) => {
        const data = results?.data || [];
        if (type === 'users' && data?.length > MAX_USER_IMPORT_ROWS) {
          setRows([{ _rowIndex: 0, _errors: [`File has ${data?.length} rows — maximum is ${MAX_USER_IMPORT_ROWS}. Please split into smaller files.`] }]);
          setIsProcessing(false);
          return;
        }
        const parsed = data?.map((row, i) => validateRow(row, type, i + 2));
        setRows(parsed);
        setIsProcessing(false);
      },
      error: () => setIsProcessing(false),
    });
  }, [type]);

  const validRows = rows?.filter(r => !r?._errors?.length);
  const errorRows = rows?.filter(r => r?._errors?.length > 0);

  const handleConfirmImport = async () => {
    if (!validRows?.length) return;
    setIsImporting(true);
    setImportResult(null);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { data: company } = await companyService?.getUserCompany();
      const companyId = company?.id;
      const userId = user?.id;

      let imported = 0;
      let skippedCount = 0;
      let details = null;

      if (type === 'users') {
        const result = await importUsers(validRows, companyId);
        imported = result?.invited || 0;
        const uErrors = result?.errors || [];
        const uWarnings = result?.warnings || [];
        // jyyb8AfJ — count warnings (previously-deactivated emails) toward
        // the skipped total and surface them in details so the admin sees
        // them in the result panel.
        skippedCount = (result?.skipped || 0) + uErrors?.length + errorRows?.length;
        details = { errors: uErrors, warnings: uWarnings };
      } else if (type === 'workers') {
        const result = await importWorkers(validRows, companyId);
        imported = result?.invited || 0;
        const wErrors = result?.errors || [];
        const wWarnings = result?.warnings || [];
        skippedCount = (result?.skipped || 0) + wErrors?.length + wWarnings?.length + errorRows?.length;
        details = { errors: wErrors, warnings: wWarnings };
        // Accurate status: only a hard failure when nothing was invited AND
        // there were real errors (not just everyone already in the company).
        if (imported === 0 && wErrors?.length > 0) {
          setImportResult({ success: false, error: `Import failed — ${wErrors?.[0]}`, details });
          return;
        }
      } else if (type === 'documents') {
        const result = await importDocuments(validRows, companyId, userId);
        imported = result?.imported || 0;
        const insertErrors = result?.errors || [];
        skippedCount = errorRows?.length + insertErrors?.length;

        if (imported === 0 && insertErrors?.length > 0) {
          const firstMsg = insertErrors?.[0]?.message || 'Insert failed';
          setImportResult({ success: false, error: `Import failed — ${firstMsg}` });
          return;
        }
      } else {
        // Sites / Tasks / Certifications / Snags all now return
        // { imported, errors } so the toast can surface real per-row
        // failures (site-name / workerEmail misses, insert errors) instead
        // of green-checking through 0 imports (DNllfNne).
        let result;
        if (type === 'sites') result = await importSites(validRows, companyId, userId);
        else if (type === 'tasks') result = await importTasks(validRows, companyId, userId);
        else if (type === 'certifications') result = await importCertifications(validRows, companyId, userId);
        else if (type === 'snags') result = await importSnags(validRows, companyId, userId);

        imported = result?.imported || 0;
        const insertErrors = result?.errors || [];
        skippedCount = errorRows?.length + insertErrors?.length;
        details = { errors: insertErrors?.map(e => e?.row ? `Row ${e?.row}: ${e?.message}` : e?.message) };

        if (imported === 0 && insertErrors?.length > 0) {
          const firstMsg = insertErrors?.[0]?.message || 'Insert failed';
          setImportResult({ success: false, error: `Import failed — ${firstMsg}`, details });
          return;
        }
      }

      setImportResult({ success: true, imported, skipped: skippedCount > 0 ? skippedCount : 0, details });
      onImportSuccess?.({ type, imported, skipped: skippedCount > 0 ? skippedCount : 0 });
      setRows([]);
      setFileName('');
    } catch (err) {
      console.error('[ImportTab] import data failed:', err);
      setImportResult({ success: false, error: 'Import failed. Please try again.' });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Template download */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-text-secondary">Download the template, fill in your data, then upload below.</p>
        </div>
        <TemplateDownloadButton type={type} />
      </div>

      {/* Drop zone */}
      <DropZone onFileSelected={handleFileSelected} isProcessing={isProcessing} fileName={fileName} />

      {/* Validation results */}
      {rows?.length > 0 && (
        <ValidationTable rows={rows} columns={columns} />
      )}

      {/* Preview table */}
      {validRows?.length > 0 && (
        <PreviewTable rows={rows} columns={columns} />
      )}

      {/* Import result */}
      {importResult && (
        <div className={`flex items-start gap-3 p-4 rounded-lg border ${
          importResult?.success
            ? 'bg-success/5 border-success/30 text-success' :'bg-error/5 border-error/30 text-error'
        }`}>
          <Icon name={importResult?.success ? 'CheckCircle' : 'XCircle'} size={18} className="shrink-0 mt-0.5" />
          <div className="min-w-0">
            {importResult?.success ? (
              <p className="text-sm font-medium">
                {(type === 'users' || type === 'workers')
                  ? `Import complete — ${importResult?.imported} invite${importResult?.imported !== 1 ? 's' : ''} sent${importResult?.skipped > 0 ? `, ${importResult?.skipped} skipped` : ''}`
                  : `Import complete — ${importResult?.imported} row${importResult?.imported !== 1 ? 's' : ''} imported${importResult?.skipped > 0 ? `, ${importResult?.skipped} skipped` : ''}`
                }
              </p>
            ) : (
              <p className="text-sm font-medium">Import failed: {importResult?.error}</p>
            )}
            {(importResult?.details?.errors?.length > 0 || importResult?.details?.warnings?.length > 0) && (
              <ul className="mt-2 space-y-1 text-xs">
                {importResult?.details?.errors?.map((m, i) => (
                  <li key={`e${i}`} className="text-error flex items-start gap-1.5">
                    <Icon name="XCircle" size={12} className="shrink-0 mt-0.5" />
                    <span>{m}</span>
                  </li>
                ))}
                {importResult?.details?.warnings?.map((m, i) => (
                  <li key={`w${i}`} className="text-amber-600 dark:text-amber-500 flex items-start gap-1.5">
                    <Icon name="AlertTriangle" size={12} className="shrink-0 mt-0.5" />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Confirm button */}
      {validRows?.length > 0 && !importResult?.success && (
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <p className="text-sm text-text-secondary">
            {validRows?.length} {type === 'users' ? 'invite' : 'row'}{validRows?.length !== 1 ? 's' : ''} ready
            {errorRows?.length > 0 ? `, ${errorRows?.length} will be skipped` : ''}
          </p>
          <button
            onClick={handleConfirmImport}
            disabled={isImporting}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent/90 disabled:opacity-60 transition-colors"
          >
            {isImporting ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> {type === 'users' ? 'Sending invites…' : 'Importing…'}</>
            ) : (
              <><Icon name={type === 'users' ? 'Send' : 'Upload'} size={16} /> {type === 'users' ? 'Send Invites' : 'Confirm Import'}</>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default ImportTab;
